import { useEffect, useState } from "react";
import { Button, Form, Input, DatePicker, Row, Col, Space } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import dayjs from "dayjs";
import { AddEditCalendarEvent } from "../../../utils/api.constant";
import useFetch from "../../../hooks/useFetch";
import { requiredFieldValidator } from "../../../utils/input.validator";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { eResultCode } from "../../../utils/enum";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";

interface Holiday {
  id?: number;
  startDate: Date | string;
  endDate: Date | string;
  title: string;
  description?: string;
}

interface HolidayFormProps {
  holiday?: Holiday;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
  id: number;
}

export default function HolidayForm({
  holiday,
  onRefreshList,
  onCloseDrawer,
  id,
}: HolidayFormProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const { post } = useFetch();
  const { onShowToast } = useToast();
  // Fetch specific holiday data if ID is provided
  const fetchSpecificHoliday = async () => {
    if (id > 0) {
      form.setFieldsValue({
        ...holiday,
        startDate: dayjs(holiday?.startDate),
        endDate: dayjs(holiday?.startDate),
      });
    }
  };

  useEffect(() => {
    if (id > 0) {
      fetchSpecificHoliday();
    } else {
      form.resetFields(); // Reset form when ID is 0 or new entry
    }
  }, [id]);

  // Handle form submission
  const onFinish = (values: Holiday) => {
    const newHoliday: Holiday = {
      ...values,
      startDate: dayjs.isDayjs(values.startDate)
        ? values.startDate.format("YYYY-MM-DD")
        : values.startDate,
      endDate: dayjs.isDayjs(values.startDate)
        ? values.startDate.format("YYYY-MM-DD")
        : values.startDate,
      // Ensure that id is set, if holiday exists use its id, otherwise generate a new id
      id: holiday?.id ?? 0, // If holiday exists, use its id, else generate a new one
    };

    // Save the holiday (call the correct API)
    saveHoliday(AddEditCalendarEvent, newHoliday);

    // Close the drawer after saving
    onCloseDrawer();
  };

  // Function to make the API call
  const saveHoliday = async (apiEndpoint: string, holiday: Holiday) => {
    setIsLoading(true);
    try {
      const response = await post(apiEndpoint, { data: holiday });
      const { dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        // If successful, show success toast and pass the holiday data back
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        onRefreshList();
        onCloseDrawer();
        setIsLoading(false);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        // Handle error if API call fails
        console.error(description);
      }
    } catch (error) {
      console.error("Error saving holiday:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="holidayForm"
        onFinish={onFinish}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", color: "white" }}
            onClick={onCloseDrawer}
          />
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  {`${id ? "Edit" : "Add"} a Holiday`}
                </h2>
              </Col>
            </Space>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="startDate"
                label="Start Date"
                rules={[{ required: true, message: "Select start date" }]}
              >
                <DatePicker format="DD-MMM-YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            {/* <Col span={12}>
              <Form.Item
                name="endDate"
                label="End Date"
                rules={[{ required: true, message: "Select end date" }]}
              >
                <DatePicker format="DD-MMM-YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col> */}
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="title"
                label="Title"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter the title"),
                  },
                ]}
              >
                <Input autoComplete="off" placeholder="Enter Holiday Title" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="description" label="Description">
                <Input.TextArea placeholder="Enter Description" rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>
        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              type="primary"
              className="btnDimensions"
              disabled={isLoading}
              onClick={form.submit}
            >
              Save
            </Button>
            <Button
              type="primary"
              className="btnDimensions"
              onClick={onCloseDrawer}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
